import React, { useState } from "react";

const ContactForm = ({ onSuccess }) => {
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [service, setService] = useState("");
  const [otherService, setOtherService] = useState("");
  const [region, setRegion] = useState("");
  const [otherRegion, setOtherRegion] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMessage("");

    const formData = new FormData(e.target);

    // Replace "Other" with the user's custom text before sending
    if (service === "Other" && otherService.trim()) {
      formData.set("service", otherService.trim());
    }
    if (region === "Other" && otherRegion.trim()) {
      formData.set("region", otherRegion.trim());
    }

    try {
      const response = await fetch(
        "https://formspree.io/f/myeyjlnz",
        {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.ok) {
        setStatus("success");
        e.target.reset();
        setService("");
        setOtherService("");
        setRegion("");
        setOtherRegion("");

        if (onSuccess) {
          onSuccess();
        }
      } else {
        let details = `HTTP ${response.status}`;
        try {
          const data = await response.json();
          if (data?.errors?.length) {
            details = data.errors.map((err) => err.message).join(", ");
          } else if (data?.error) {
            details = data.error;
          }
        } catch (parseErr) {
          // response wasn't JSON, keep the generic status-based message
        }
        console.error("Formspree submission failed:", response.status, details);
        setErrorMessage(details);
        setStatus("error");
      }
    } catch (error) {
      console.error("Network/fetch error submitting form:", error);
      setErrorMessage(error.message || "Network error");
      setStatus("error");
    }
  };

  return (
    <div className="w-full flex items-center justify-center">
      <div
        className="
          w-full 
          rounded-xl sm:rounded-2xl 
          bg-gradient-to-br from-[rgb(232,198,236)] via-[#F3E9FB] to-[#f3b5f0] 
          border border-purple-100 
          shadow-xl 
          px-4 py-3 
          sm:px-6 sm:py-4 
          md:px-8 md:py-4 
          lg:px-10 lg:py-5 
        "
      >
        {/* Heading */}
        <div className="mb-3 sm:mb-4 text-center">
          <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
            Let's Discuss Your{" "}
            <span className="text-purple-700">Project</span>
          </h4>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-3.5">
          {/* First Name + Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                required
                className="
                  w-full min-w-0 rounded-xl border border-gray-200
                  bg-white px-4 py-2 sm:py-2.5
                  text-sm sm:text-base text-gray-700 shadow-sm
                  transition-all duration-300
                  focus:border-purple-600 focus:ring-4
                  focus:ring-purple-100 outline-none
                "
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                placeholder="Last Name "
                required
                className="
                  w-full min-w-0 rounded-xl border border-gray-200
                  bg-white px-4 py-2 sm:py-2.5
                  text-sm sm:text-base text-gray-700 shadow-sm
                  transition-all duration-300
                  focus:border-purple-600 focus:ring-4
                  focus:ring-purple-100 outline-none
                "
              />
            </div>
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Phone Number
              </label>
              <div className="flex w-full">
                <select
                  name="countryCode"
                  defaultValue="+92"
                  className="
                    w-[80px] sm:w-[95px] shrink-0
                    rounded-l-xl border border-r-0 border-gray-200
                    bg-white px-2 py-2 sm:py-2.5
                    text-sm sm:text-base text-gray-700
                    outline-none focus:border-purple-600
                    focus:ring-4 focus:ring-purple-100
                  "
                >
                  <option value="+92">+92</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+49">+49</option>
                  <option value="+33">+33</option>
                  <option value="+61">+61</option>
                  <option value="+64">+64</option>
                  <option value="+971">+971</option>
                </select>

                <input
                  type="tel"
                  name="phone"
                  placeholder="300 1234567"
                  required
                  className="
                    w-full min-w-0 rounded-r-xl
                    border border-gray-200 bg-white
                    px-3 py-2 sm:py-2.5
                    text-sm sm:text-base text-gray-700
                    outline-none focus:border-purple-600
                    focus:ring-4 focus:ring-purple-100
                  "
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                required
                className="
                  w-full min-w-0 rounded-xl border border-gray-200
                  bg-white px-4 py-2 sm:py-2.5
                  text-sm sm:text-base text-gray-700 shadow-sm
                  transition-all duration-300
                  focus:border-purple-600 focus:ring-4
                  focus:ring-purple-100 outline-none
                "
              />
            </div>
          </div>

          {/* Service + Region */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Service Required
              </label>
              <select
                name="service"
                value={service}
                onChange={(e) => setService(e.target.value)}
                required
                className="
                  w-full min-w-0 rounded-xl border border-gray-200
                  bg-white px-4 py-2 sm:py-2.5
                  text-sm sm:text-base text-gray-700 shadow-sm
                  transition-all duration-300
                  focus:border-purple-600 focus:ring-4
                  focus:ring-purple-100 outline-none
                "
              >
                <option value="" disabled>Select a Service</option>
                <option>Mechanical Engineering</option>
                <option>Electrical Engineering</option>
                <option>Industrial Design</option>
                <option>PCB Design</option>
                <option>CAD Design</option>
                <option>3D Modeling</option>
                <option>3D Rendering</option>
                <option>Product Development</option>
                <option>Prototype Development</option>
                <option>Manufacturing Support</option>
                <option>Consultation</option>
                <option>Other</option>
              </select>

              {service === "Other" && (
                <input
                  type="text"
                  name="otherService"
                  placeholder="Please specify the service you need"
                  value={otherService}
                  onChange={(e) => setOtherService(e.target.value)}
                  required
                  className="
                    mt-2 w-full min-w-0 rounded-xl border border-gray-200
                    bg-white px-4 py-2 sm:py-2.5
                    text-sm sm:text-base text-gray-700 shadow-sm
                    transition-all duration-300
                    focus:border-purple-600 focus:ring-4
                    focus:ring-purple-100 outline-none
                  "
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Region
              </label>
              <select
                name="region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                required
                className="
                  w-full min-w-0 rounded-xl border border-gray-200
                  bg-white px-4 py-2 sm:py-2.5
                  text-sm sm:text-base text-gray-700 shadow-sm
                  transition-all duration-300
                  focus:border-purple-600 focus:ring-4
                  focus:ring-purple-100 outline-none
                "
              >
                <option value="" disabled>Select Your Region</option>
                <option>United States</option>
                <option>Canada</option>
                <option>United Kingdom</option>
                <option>Germany</option>
                <option>France</option>
                <option>Australia</option>
                <option>New Zealand</option>
                <option>United Arab Emirates</option>
                <option>Other</option>
              </select>

              {region === "Other" && (
                <input
                  type="text"
                  name="otherRegion"
                  placeholder="Please specify your region"
                  value={otherRegion}
                  onChange={(e) => setOtherRegion(e.target.value)}
                  required
                  className="
                    mt-2 w-full min-w-0 rounded-xl border border-gray-200
                    bg-white px-4 py-2 sm:py-2.5
                    text-sm sm:text-base text-gray-700 shadow-sm
                    transition-all duration-300
                    focus:border-purple-600 focus:ring-4
                    focus:ring-purple-100 outline-none
                  "
                />
              )}
            </div>
          </div>

          {/* Project Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Project Details
            </label>
            <textarea
              rows={3}
              name="message"
              placeholder="Tell us about your project, requirements, timeline, or any specific details..."
              required
              className="
                w-full min-w-0 rounded-xl border border-gray-200
                bg-white px-4 py-2.5
                text-sm sm:text-base text-gray-700 shadow-sm
                resize-none transition-all duration-300
                focus:border-purple-600 focus:ring-4
                focus:ring-purple-100 outline-none
              "
            />
          </div>

          {/* Attachment note (file upload removed — Formspree plan doesn't permit uploads) */}
          <div
            className="
              rounded-xl border border-purple-100
              bg-white/70 px-4 py-3
              text-center
            "
          >
            <p className="text-sm text-gray-600">
              Have a document, portfolio, or file to share?{" "}
              <span className="block sm:inline">
                Please email it directly to{" "}
                <a
                  href="mailto:hr@keymech.org"
                  className="font-medium text-purple-600 hover:underline"
                >
                  hr@keymech.org
                </a>
                
              </span>
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={status === "sending"}
            className="
              w-full rounded-xl
              bg-gradient-to-r from-purple-700
              via-purple-600 to-pink-500
              py-2.5 sm:py-3 px-4
              text-sm sm:text-base
              font-semibold text-white shadow-lg
              hover:shadow-purple-300
              hover:scale-[1.01]
              active:scale-[0.99]
              transition-all duration-300
              disabled:opacity-60
              disabled:cursor-not-allowed
              disabled:hover:scale-100
            "
          >
            {status === "sending" ? "Sending..." : "Send Message"}
          </button>

          {status === "success" && (
            <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-2.5">
              <p className="text-sm text-center text-green-600 font-medium">
                Thanks! Your message has been sent successfully.
              </p>
            </div>
          )}

          {status === "error" && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5">
              <p className="text-sm text-center text-red-600 font-medium">
                Something went wrong. Please try again.
                {errorMessage && (
                  <span className="block mt-1 text-xs text-red-400">
                    ({errorMessage})
                  </span>
                )}
              </p>
            </div>
          )}

          <p className="text-center text-[11px] text-gray-400">
            By submitting this form, you agree to be contacted regarding your project.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;